import React, { useState } from 'react';
import { CaretDown } from '@phosphor-icons/react';

/**
 * ReadDeeper — Wave 4 Phase 1B P1
 *
 * A simple inline disclosure wrapper used on the report detail page to
 * collapse the verbose educational content (full Document Summary essay,
 * Plain-language explanation, Why-this-matters, Things to clarify,
 * Providers & Dates) under a single "Read deeper" toggle.
 *
 * The point: the user's first viewport already answers what matters,
 * what changed, what Kindra remembered, what to ask. Everything else is
 * one tap away — present but not screaming.
 *
 * Usage:
 *   <ReadDeeper>
 *     <DocumentSummaryCard ... />
 *     <ThingsToClarifyCard ... />
 *   </ReadDeeper>
 */
export const ReadDeeper = ({ children, label = 'Read deeper', defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid="read-deeper-wrapper">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full inline-flex items-center justify-between gap-2 px-4 py-3 min-h-[44px] rounded-xl border border-slate-100 bg-transparent hover:bg-white hover:border-slate-200 transition-colors text-[12.5px] font-medium text-slate-500 hover:text-slate-700"
        aria-expanded={open}
        data-testid="read-deeper-toggle"
      >
        <span>{open ? 'Show less' : label}</span>
        <CaretDown
          size={12}
          weight="bold"
          className={`text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="mt-4 space-y-6" data-testid="read-deeper-content">
          {children}
        </div>
      )}
    </div>
  );
};

export default ReadDeeper;
